import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import connect from '@app/utils/connect';
import combineActions from '@app/utils/combineActions';

import sportsApi from '@app/utils/sportsApi';
import waitFor from '@app/utils/waitFor';

import {
    isRunning,
    isCompleted,
    isNotStarted,
} from '@app/components/automated-param-modal/options';
import updateCollection from '@app/actions/collections/updateCollection';
import sendNotification from '@app/actions/notifications/send';
import { Client, Collection } from '@app/types/api';
import { Notification } from '@app/components/notifications/Notifications';
import { CollectionStore } from '@app/types/store';
import { Alert } from '@app/ui';

// SP-8431: poll a still-processing collection at most once per interval.
const POLL_INTERVAL_MS = 3000;

type CollectionsSnackProps = {
    client: Client;
    collections: CollectionStore;
    updateCollection: (
        id: number,
        values: Partial<Collection>,
    ) => Promise<void>;
    sendNotification: (
        text: string,
        options?: Notification['options'],
        shouldAutoClose?: boolean,
        hasActionButton?: boolean,
    ) => void;
};

const CollectionsSnack = ({
    client,
    collections,
    updateCollection,
    sendNotification,
}: CollectionsSnackProps) => {
    const { t } = useTranslation(['tools', 'common']);

    const currentCollections = useMemo(
        () =>
            Object.values(collections?.list.items || []).filter(
                c => c.jobStatus == isRunning || c.jobStatus == isNotStarted,
            ),
        [collections],
    );

    // SP-8431: stable trigger — only (re)start polling when the SET of in-flight
    // collection ids changes, not on every render / unrelated store write.
    const pollKey = useMemo(
        () =>
            currentCollections
                .map(c => c.id)
                .sort((a, b) => a - b)
                .join(','),
        [currentCollections],
    );

    const [isShowing, setShowing] = useState(true);

    const _close = () => {
        setShowing(false);
    };

    // One timer + "currently polling" guard per collection id, so we never run
    // more than one poller per collection and can always clean them up.
    const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
        new Map(),
    );
    const activeRef = useRef<Set<number>>(new Set());

    // Keep the latest props in a ref so the recursive poller closure stays
    // stable (doesn't need to re-run the effect when action props change).
    const depsRef = useRef({ client, updateCollection, sendNotification, t });
    depsRef.current = { client, updateCollection, sendNotification, t };

    useEffect(() => {
        const stopAll = () => {
            timersRef.current.forEach(tid => clearTimeout(tid));
            timersRef.current.clear();
            activeRef.current.clear();
        };

        // Dismissing the snack stops all polling.
        if (!isShowing) {
            stopAll();
            return;
        }

        const inFlight = new Set(currentCollections.map(c => c.id));

        // Stop pollers for collections that are no longer in flight.
        Array.from(activeRef.current).forEach(id => {
            if (!inFlight.has(id)) {
                const tid = timersRef.current.get(id);
                if (tid) clearTimeout(tid);
                timersRef.current.delete(id);
                activeRef.current.delete(id);
            }
        });

        const poll = (id: number) => async () => {
            if (!activeRef.current.has(id)) return; // stopped / cleaned up
            const {
                client: c,
                updateCollection: update,
                sendNotification: notify,
                t: translate,
            } = depsRef.current;
            try {
                const col: Collection = await sportsApi
                    .url(`/clients/${c.id}/collections/${id}`)
                    .get()
                    .json();

                if (!activeRef.current.has(id)) return; // stopped while in flight

                const { jobStatus, postCount } = col;

                if (jobStatus == isCompleted) {
                    activeRef.current.delete(id);
                    timersRef.current.delete(id);
                    await update(id, { postCount, jobStatus });

                    // When the last in-flight collection finishes, surface success once.
                    if (activeRef.current.size === 0) {
                        await waitFor(1000);
                        notify(translate('tools:collections.snack.success'), {
                            description: translate(
                                'tools:collections.snack.successDesc',
                            ),
                        });
                    }
                } else {
                    const tid = setTimeout(poll(id), POLL_INTERVAL_MS);
                    timersRef.current.set(id, tid);
                }
            } catch (e) {
                // Stop polling this id on error to avoid a hot error loop.
                activeRef.current.delete(id);
                timersRef.current.delete(id);
                // eslint-disable-next-line no-console
                console.error('CollectionsSnack: poll failed', e);
            }
        };

        // Start a single poller for each newly in-flight collection.
        currentCollections.forEach(c => {
            if (!activeRef.current.has(c.id)) {
                activeRef.current.add(c.id);
                poll(c.id)();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollKey, isShowing]);

    // Stop every poller when the component unmounts.
    useEffect(() => {
        const timers = timersRef.current;
        const active = activeRef.current;
        return () => {
            timers.forEach(tid => clearTimeout(tid));
            timers.clear();
            active.clear();
        };
    }, []);

    const collectionsStatusInfo = useMemo(() => {
        if (!isShowing) return null;
        if (!collections) return null;
        if (currentCollections.length === 0) return null;

        return currentCollections.map((c, i) => {
            const { name } = c;

            return (
                <p
                    style={{ fontWeight: 400 }}
                    key={`${name}_${i}`}
                    className="gds-text--body-md -ellipsis"
                >
                    {name + t('tools:collections.snack.processing')}
                </p>
            );
        });
    }, [collections, currentCollections, isShowing, t]);

    const collectionsSnackInfo = useMemo(() => {
        let snackTitle = '';
        let snackIcon = null;
        const snackType = 'info';

        if (!isShowing || !collections || currentCollections.length === 0)
            return {
                snackTitle,
                snackIcon,
                snackType,
            };

        if (currentCollections.some(col => col.jobStatus !== isCompleted)) {
            snackTitle = t('tools:collections.snack.populating');
            snackIcon = (
                <i
                    style={{ marginTop: '0.77rem' }}
                    className="fa-duotone fa-solid fa-spinner-third fa-spin"
                />
            );
        }

        return {
            snackTitle,
            snackIcon,
            snackType,
        };
    }, [collections, currentCollections, isShowing]);

    if (!collections) return null;
    if (currentCollections.length == 0) return null;

    return (
        <>
            {isShowing && collectionsStatusInfo?.length && (
                <div
                    style={{
                        position: 'fixed',
                        top: 80,
                        right: 50,
                        zIndex: 9999,
                    }}
                >
                    <Alert
                        style={{ minWidth: '30rem' }}
                        key="collections-snack"
                        closable
                        showIcon
                        message={collectionsSnackInfo.snackTitle}
                        description={collectionsStatusInfo}
                        icon={collectionsSnackInfo.snackIcon}
                        type="info"
                        afterClose={() => _close()}
                    />
                </div>
            )}
        </>
    );
};

CollectionsSnack.propTypes = {
    collections: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
    updateCollection: PropTypes.func.isRequired,
    sendNotification: PropTypes.func.isRequired,
};

export default connect(
    ['collections', 'client'],
    combineActions({
        updateCollection,
        sendNotification,
    }),
)(CollectionsSnack);
